/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { IISolarBatteryOutlookResponse } from "@/contract/generated/electrify-v1";
import { findMedianValue } from "@/utils";

interface GetMedianSizeIndexProps {
  solarBatteryOutlook: Array<IISolarBatteryOutlookResponse>;
}

interface IsOutlierProps {
  solarBatterySystem: IISolarBatteryOutlookResponse;
}

type DecimalValue = {
  value: number;
  position: number;
};

export const getMedianSizeIndex = ({
  solarBatteryOutlook,
}: GetMedianSizeIndexProps) => {
  if (solarBatteryOutlook && solarBatteryOutlook.length) {
    const filteredSystemData = solarBatteryOutlook.filter(
      ({ size }) => size !== undefined
    );

    // If the number of filtered system data is an odd number, just get the middle index
    if (filteredSystemData.length % 2 !== 0) {
      return Math.ceil(filteredSystemData.length / 2) - 1;
    }

    const systemSizeList = filteredSystemData.map(({ size }) => size!);

    const medianValue = findMedianValue(systemSizeList);

    if (!medianValue) {
      return -1;
    }

    let medianIndex = 0;

    // The system size index that is defaulted is set to the median option or the lower side of median
    for (const systemSize of filteredSystemData) {
      const { size } = systemSize;
      if (size! > medianValue) {
        break;
      }

      medianIndex = solarBatteryOutlook.indexOf(systemSize);
    }

    return medianIndex;
  }

  return -1;
};

export const toDecimal = (decimalValue: DecimalValue): number => {
  return decimalValue.value / Math.pow(10, decimalValue.position);
};

export const fromDecimal = (value: number, position: number): DecimalValue => {
  return {
    value: Math.round(value * Math.pow(10, position)),
    position,
  };
};

export const isOutlier = ({ solarBatterySystem }: IsOutlierProps): boolean => {
  const {
    averageDailyGenerationAccurassi = 0,
    dayTimeUsageInKwh = 0,
    percentageDailyConsumption = 0,
  } = solarBatterySystem;

  const minBatterySize = 5;

  const generationAccurassi = fromDecimal(
    Number(averageDailyGenerationAccurassi),
    2
  );
  const dayTimeUsage = fromDecimal(Number(dayTimeUsageInKwh), 2);

  const excessGeneration = toDecimal({
    value: generationAccurassi.value - dayTimeUsage.value,
    position: 2,
  });

  return percentageDailyConsumption == 100 && excessGeneration < minBatterySize;
};
